import { signal } from '@preact/signals';
import { api } from '../api/client';

export const TEXT_SCALE_MIN = 1;
export const TEXT_SCALE_MAX = 1.5;
export const TEXT_SCALE_STEP = 0.1;

const STORAGE_KEY = 'jarvis.textScale';

function normalizeTextScale(value: unknown): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return TEXT_SCALE_MIN;
  const stepped = Math.round(numeric / TEXT_SCALE_STEP) * TEXT_SCALE_STEP;
  return Math.min(TEXT_SCALE_MAX, Math.max(TEXT_SCALE_MIN, Number(stepped.toFixed(1))));
}

function readStoredTextScale(): number {
  try {
    return normalizeTextScale(localStorage.getItem(STORAGE_KEY));
  } catch {
    return TEXT_SCALE_MIN;
  }
}

function applyDocumentScale(scale: number): void {
  document.documentElement.style.setProperty('--app-text-scale', String(scale));
}

export const textScale = signal(readStoredTextScale());
applyDocumentScale(textScale.value);

export function previewTextScale(value: unknown): number {
  const scale = normalizeTextScale(value);
  textScale.value = scale;
  applyDocumentScale(scale);
  try {
    localStorage.setItem(STORAGE_KEY, String(scale));
  } catch {
    // The live document still receives the setting when browser storage is unavailable.
  }
  return scale;
}

export async function saveTextScale(value: unknown): Promise<number> {
  const scale = previewTextScale(value);
  const saved = await api.updatePreferences({ textScale: scale });
  return previewTextScale(saved.textScale);
}

export async function syncTextScale(): Promise<number> {
  const preferences = await api.getPreferences();
  return previewTextScale(preferences.textScale);
}
